import { UseMutationResult, useMutation } from "@tanstack/react-query";
import axiosInstance from "../../shared/service/axios";
import { AxiosError } from "axios";
import {
  CustomerAllMaintenanceResponse,
  CustomerAllPaymentOrdersRequest,
  CustomerAllPaymentRequest,
  CustomerAllPaymentResponse,
  CustomerLastMaintenanceRequest,
  CustomerLastMaintenanceResponse,
  CustomerMaintenanceRequest,
  CustomerMaintenanceUpdateRequest,
  CustomerPaymentAddRequest,
  CustomerPaymentRequest,
  CustomerPaymentResponse,
  CustomerPaymentUpdateRequest,
  CustomerProfileDetailRequest,
  CustomerProfileResponse,
  CustomerWarrantyUpdateRequest,
} from "./CustomerProfile.constants";

export const useCustomerProfileMutation = (): UseMutationResult<
  CustomerProfileResponse,
  AxiosError,
  any
> =>
  useMutation<CustomerProfileResponse, AxiosError, any>(
    async (requestValues: CustomerProfileDetailRequest) => {
      const { data, status } = await axiosInstance.get(
        "customer/" + requestValues.id + "/"
      );

      return data;
    }
  );

export const useCustomerAllPaymentMutation = (): UseMutationResult<
  CustomerAllPaymentResponse,
  AxiosError,
  CustomerAllPaymentRequest
> =>
  useMutation<
    CustomerAllPaymentResponse,
    AxiosError,
    CustomerAllPaymentRequest
  >(async (requestValues: CustomerAllPaymentRequest) => {
    const { data, status } = await axiosInstance.get(
      "customer/payment/" + requestValues.id + "/allPayments/",
      {
        params: {
          page_size: 999999,
        },
      }
    );
    data.status = status;

    return data;
  });

export const useCustomerAllPaymentOrdersMutation = (): UseMutationResult<
  CustomerAllPaymentResponse,
  AxiosError,
  CustomerAllPaymentOrdersRequest
> =>
  useMutation<
    CustomerAllPaymentResponse,
    AxiosError,
    CustomerAllPaymentOrdersRequest
  >(async (requestValues: CustomerAllPaymentOrdersRequest) => {
    const { data, status } = await axiosInstance.get(
      "customer/payment/" + requestValues.id + "/allPaidOrders/"
    );
    data.status = status;

    return data;
  });

export const useCustomerPaymentMutation = (): UseMutationResult<
  CustomerPaymentResponse,
  AxiosError,
  CustomerPaymentRequest
> =>
  useMutation<CustomerPaymentResponse, AxiosError, CustomerPaymentRequest>(
    async (requestValues: CustomerPaymentRequest) => {
      const { data, status } = await axiosInstance.post(
        "customer/payment/",
        requestValues
      );
      data.status = status;

      return data;
    }
  );

export const useCustomerLastMaintenanceMutation = (): UseMutationResult<
  CustomerLastMaintenanceResponse,
  AxiosError,
  CustomerLastMaintenanceRequest
> =>
  useMutation<
    CustomerLastMaintenanceResponse,
    AxiosError,
    CustomerLastMaintenanceRequest
  >(async (requestValues: CustomerLastMaintenanceRequest) => {
    const { data, status } = await axiosInstance.get(
      "customer/" + requestValues.id + "/maintenance/last/"
    );
    data.status = status;

    return data;
  });

export const useCustomerAllMaintenanceMutation = (): UseMutationResult<
  CustomerAllMaintenanceResponse,
  AxiosError,
  CustomerLastMaintenanceRequest
> =>
  useMutation<
    CustomerAllMaintenanceResponse,
    AxiosError,
    CustomerLastMaintenanceRequest
  >(async (requestValues: CustomerLastMaintenanceRequest) => {
    const { data, status } = await axiosInstance.get(
      "customer/" + requestValues.id + "/allMaintenances/"
    );
    data.status = status;

    return data;
  });

export const useCustomerMaintenanceMutation = (): UseMutationResult<
  any,
  AxiosError,
  CustomerMaintenanceRequest
> =>
  useMutation<any, AxiosError, CustomerMaintenanceRequest>(
    async (requestValues: CustomerMaintenanceRequest) => {
      let { data, status } = await axiosInstance.post(
        "customer/" + requestValues.customerId + "/maintenance/add/",
        requestValues
      );

      data.status = status;

      return data;
    }
  );

export const useCustomerMaintenanceUpdateMutation = (): UseMutationResult<
  any,
  AxiosError,
  CustomerMaintenanceUpdateRequest
> =>
  useMutation<any, AxiosError, CustomerMaintenanceUpdateRequest>(
    async (requestValues: CustomerMaintenanceUpdateRequest) => {
      let { data, status } = await axiosInstance.put(
        "maintenance/" + requestValues.maintenanceId + "/",
        requestValues
      );

      data.status = status;

      return data;
    }
  );

export const useCustomerWarrantyUpdateMutation = (): UseMutationResult<
  any,
  AxiosError,
  CustomerWarrantyUpdateRequest
> =>
  useMutation<any, AxiosError, CustomerWarrantyUpdateRequest>(
    async (requestValues: CustomerWarrantyUpdateRequest) => {
      let { data, status } = await axiosInstance.put(
        "customer-device/" + requestValues.customerDeviceId + "/",
        requestValues
      );

      data.status = status;

      return data;
    }
  );

export const useCustomerPaymentAddMutation = (): UseMutationResult<
  any,
  AxiosError,
  CustomerPaymentAddRequest
> =>
  useMutation<any, AxiosError, CustomerPaymentAddRequest>(
    async (requestValues: CustomerPaymentAddRequest) => {
      let { data, status } = await axiosInstance.post(
        "payment/paidOrder/",
        requestValues
      );

      data.status = status;

      return data;
    }
  );

export const useCustomerPaymentUpdateMutation = (): UseMutationResult<
  any,
  AxiosError,
  CustomerPaymentUpdateRequest
> =>
  useMutation<any, AxiosError, CustomerPaymentUpdateRequest>(
    async (requestValues: CustomerPaymentUpdateRequest) => {
      let { data, status } = await axiosInstance.put(
        "customer/payment/" + requestValues.paymentId + "/",
        requestValues
      );

      data.status = status;

      return data;
    }
  );

export const useCustomerPaymentInstallmentAddMutation = (): UseMutationResult<
  any,
  AxiosError,
  CustomerPaymentAddRequest
> =>
  useMutation<any, AxiosError, CustomerPaymentAddRequest>(
    async (requestValues: CustomerPaymentAddRequest) => {
      let { data, status } = await axiosInstance.put(
        "payment/paidOrder/" + requestValues.paymentId + "/",
        requestValues
      );

      data.status = status;

      return data;
    }
  );
